import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Empty,
  Input,
  List,
  Spin,
  message,
} from 'antd';
import {
  AimOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FormOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { debounce } from 'lodash';
import apiClient from 'services/apiClient';
import LocationCoordinateForm from './LocationCoordinateForm';
import CustomDrawer from "components/shared-components/CustomDrawer";

const EMPTY_LOCATION = {
  latitude: '',
  longitude: '',
  name: '',
  address: '',
};

const formatCoords = (location) => ({
  latitude: Number(location.latitude),
  longitude: Number(location.longitude),
  name: location.name || '',
  address: location.address || '',
});

const buildMapEmbedUrl = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  const delta = 0.01;
  const bbox = [
    longitude - delta,
    latitude - delta,
    longitude + delta,
    latitude + delta,
  ].join('%2C');

  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude}%2C${longitude}`;
};

const LocationSendDrawer = ({ open, onClose, onSendLocation, isMobile = false }) => {
  const [view, setView] = useState('menu');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 });
  const [manualLocation, setManualLocation] = useState(EMPTY_LOCATION);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const searchRequestId = useRef(0);

  const resetState = useCallback(() => {
    setView('menu');
    setLoading(false);
    setSearching(false);
    setSearchQuery('');
    setSearchResults([]);
    setManualLocation(EMPTY_LOCATION);
    setSelectedPlace(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const sendLocation = async (location) => {
    if (!location?.latitude || !location?.longitude) {
      message.error('Latitude and longitude are required');
      return;
    }

    setLoading(true);
    try {
      await onSendLocation?.(formatCoords(location));
      handleClose();
    } catch (error) {
      message.error(error?.response?.data?.error || 'Failed to send location');
    } finally {
      setLoading(false);
    }
  };

  const sendCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      message.error('Geolocation is not supported in this browser');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          await onSendLocation?.({
            latitude,
            longitude,
            name: 'Current Location',
            address: '',
          });
          handleClose();
        } catch (error) {
          message.error(error?.response?.data?.error || 'Failed to send location');
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        setLoading(false);
        if (error.code === error.PERMISSION_DENIED) {
          message.error('Location permission denied');
          return;
        }
        message.error('Unable to get your current location');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, [onSendLocation]);

  const searchPlaces = useCallback(async (query, lat, lng) => {
    const trimmed = query?.trim();
    if (!trimmed || trimmed.length < 2) {
      setSearchResults([]);
      return;
    }

    const requestId = ++searchRequestId.current;
    setSearching(true);

    try {
      const response = await apiClient.get('places/search', {
        params: {
          q: trimmed,
          lat,
          lng,
        },
      });

      if (requestId !== searchRequestId.current) {
        return;
      }

      setSearchResults(response.data?.items || []);
    } catch (error) {
      if (requestId === searchRequestId.current) {
        message.error('Location search failed');
        setSearchResults([]);
      }
    } finally {
      if (requestId === searchRequestId.current) {
        setSearching(false);
      }
    }
  }, []);

  const debouncedSearch = useRef(
    debounce((query, lat, lng) => {
      searchPlaces(query, lat, lng);
    }, 400)
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (view !== 'map') {
      return;
    }

    const lat = selectedPlace?.latitude || mapCenter.lat;
    const lng = selectedPlace?.longitude || mapCenter.lng;
    debouncedSearch(searchQuery, lat, lng);
  }, [searchQuery, view, mapCenter.lat, mapCenter.lng, selectedPlace, debouncedSearch]);

  const openMapPicker = () => {
    setView('map');
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setMapCenter({ lat, lng });
        setSelectedPlace({
          id: 'current-map-center',
          latitude: lat,
          longitude: lng,
          name: 'Selected location',
          address: '',
        });
      },
      () => { },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleSelectPlace = (item) => {
    setSelectedPlace(item);
    setMapCenter({
      lat: item.latitude,
      lng: item.longitude,
    });
  };

  const menuItems = [
    {
      key: 'current',
      icon: <AimOutlined style={{ color: '#25d366', fontSize: 20 }} />,
      title: 'Send your current location',
      subtitle: 'Uses device GPS',
      onClick: sendCurrentLocation,
    },
    {
      key: 'map',
      icon: <EnvironmentOutlined style={{ color: '#25d366', fontSize: 20 }} />,
      title: 'Choose on map',
      subtitle: 'Search and pick a place',
      onClick: openMapPicker,
    },
    {
      key: 'manual',
      icon: <FormOutlined style={{ color: '#25d366', fontSize: 20 }} />,
      title: 'Enter coordinates',
      subtitle: 'Latitude, longitude, name, address',
      onClick: () => setView('manual'),
    },
  ];

  const renderMenu = () => (
    <List
      dataSource={menuItems}
      renderItem={(item) => (
        <List.Item
          style={{ cursor: 'pointer', padding: '14px 4px' }}
          onClick={item.onClick}
        >
          <List.Item.Meta
            avatar={item.icon}
            title={<span style={{ fontWeight: 600 }}>{item.title}</span>}
            description={item.subtitle}
          />
        </List.Item>
      )}
    />
  );

  const renderMapPicker = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search or enter an address"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onPressEnter={() => {
          const lat = selectedPlace?.latitude || mapCenter.lat;
          const lng = selectedPlace?.longitude || mapCenter.lng;
          searchPlaces(searchQuery, lat, lng);
        }}
      />
      <div style={{ borderRadius: 12, overflow: 'hidden', height: 220, background: '#111' }}>
        <iframe
          title="Location map"
          src={buildMapEmbedUrl(
            selectedPlace?.latitude || mapCenter.lat,
            selectedPlace?.longitude || mapCenter.lng
          )}
          style={{ border: 0, width: '100%', height: '100%' }}
          loading="lazy"
        />
      </div>
      <Button
        type="text"
        icon={<AimOutlined />}
        onClick={sendCurrentLocation}
        style={{ justifyContent: 'flex-start', color: '#25d366', fontWeight: 600 }}
      >
        Send your current location
      </Button>
      <Spin spinning={searching}>
        {searchResults.length ? (
          <List
            size="small"
            dataSource={searchResults}
            style={{ maxHeight: 220, overflowY: 'auto' }}
            renderItem={(item) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  background: selectedPlace?.id === item.id ? '#f6ffed' : 'transparent',
                  borderRadius: 8,
                  padding: '10px 8px',
                }}
                onClick={() => handleSelectPlace(item)}
              >
                <List.Item.Meta
                  avatar={<EnvironmentOutlined />}
                  title={item.name}
                  description={item.address}
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchQuery.trim().length >= 2 ? 'No places found' : 'Type to search places'}
          />
        )}
      </Spin>
      <Button
        type="primary"
        block
        loading={loading}
        disabled={!selectedPlace}
        onClick={() => sendLocation(selectedPlace)}
        style={{ background: '#25d366', marginTop: 'auto' }}
      >
        Send location
      </Button>
    </div>
  );

  const renderManual = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <LocationCoordinateForm
        location={manualLocation}
        setLocation={setManualLocation}
        onSubmit={() => sendLocation(manualLocation)}
      />
      <Button
        type="primary"
        block
        loading={loading}
        onClick={() => sendLocation(manualLocation)}
        style={{ background: '#25d366', marginTop: 'auto' }}
      >
        Send location
      </Button>
    </div>
  );

  const title = view === 'menu'
    ? 'Send location'
    : view === 'map'
      ? 'Choose on map'
      : 'Enter coordinates';

  return (
    <CustomDrawer
      title={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {view !== 'menu' && (
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => setView('menu')}
            />
          )}
          <span>{title}</span>
        </div>
      )}
      width={420}
      open={open}
      onClose={handleClose}
      destroyOnClose
      closeIcon={<CloseOutlined />}
      extra={loading ? <Spin size="small" /> : null}
    >
      {view === 'menu' && renderMenu()}
      {view === 'map' && renderMapPicker()}
      {view === 'manual' && renderManual()}
    </CustomDrawer>
  );
};

export default LocationSendDrawer;
